import { HubType } from '../types/SignalR';
import { HubConnectionState } from '@microsoft/signalr';
import { useReducer, Dispatch, useMemo } from 'react';

type SocketStoreState = {
  connectionStates: Record<HubType, HubConnectionState | null>;
};

type SocketStoreAction = {
  type: 'ConnectionStates';
  payload: {
    hub: HubType;
    connectionState: HubConnectionState;
  };
};

export type SocketStore = {
  reducer: [SocketStoreState, Dispatch<SocketStoreAction>];
  connectionState: HubConnectionState | null;
};

const reducer = (state: SocketStoreState, action: SocketStoreAction) => {
  switch (action.type) {
    case 'ConnectionStates':
      const newConnections = { ...state.connectionStates };
      newConnections[action.payload.hub] = action.payload.connectionState;
      return { ...state, connectionStates: newConnections };
    default:
      throw new Error('Could not find state type in Socket store.');
  }
};

const useSocketStore = (): SocketStore => {
  const [state, dispatch] = useReducer(reducer, {
    connectionStates: {
      paymentEventHub: null,
    },
  });

  const connectionState = useMemo(() => {
    const states = Object.keys(state.connectionStates)
      .filter((key) => state.connectionStates[key as HubType])
      .map((key) => state.connectionStates[key as HubType]);

    if (states.length === 0) {
      // No socket connection exists
      return null;
    }

    // Disconnected if any hub is disconnected
    if (states.some((s) => s === HubConnectionState.Disconnected)) {
      return HubConnectionState.Disconnected;
    }

    // Connecting if no hub is disconnected and any hub is connecting
    if (states.some((s) => s === HubConnectionState.Connecting)) {
      return HubConnectionState.Connecting;
    }

    // Reconnecting if no hub is disconnected, no hub is connecting and any hub is reconnectins
    if (states.some((s) => s === HubConnectionState.Reconnecting)) {
      return HubConnectionState.Reconnecting;
    }

    // Connected if all hubs are connected
    if (states.every((s) => s === HubConnectionState.Connected)) {
      return HubConnectionState.Connected;
    }

    // If no criteria above is fulfilled the state is disconnected
    return HubConnectionState.Disconnected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.connectionStates.paymentEventHub]);

  return {
    reducer: [state, dispatch],
    connectionState,
  };
};

export default useSocketStore;
